import React, { useEffect, useRef, useState } from "react";
import {
  IonModal,
  IonHeader,
  IonContent,
  IonToolbar,
  IonTitle,
} from "@ionic/react";
import closeIcon from "../../../assets/imgs/close.svg";
import advancedStatsHeading from "../../../assets/imgs/advanced-stats-heading.svg";
import advancedStatsSearch from "../../../assets/imgs/advanced-stats-search.svg";
import { useSelector } from "react-redux";
import { actions } from "../../../Redux/store";
import HorizontalFilterBar from "./HorizontalFilterBar";
import { useFirebaseRemoteParams } from "../../../Context/FirebaseContext/FirebaseContext";
import { motion, AnimatePresence } from "framer-motion";

// Define TypeScript interfaces for the statGlossary structure
interface StatItem {
  column_name: string;
  display_name: string;
  description: string;
}

interface StatGlossary {
  [category: string]: StatItem[];
}

export default function AdvancedStatsModal() {
  const modal = useRef<HTMLIonModalElement>(null);
  const isOpen = useSelector(
    (sate: any) => sate.modal.advancedStatsModal?.open,
  );
  const toggle = () => {
    actions.modal.closeAdvancedStatsModal(null);
  };
  const [statGlossary, setStatGlossary] = useState<StatGlossary | undefined>();
  const [searchTerm, setSearchTerm] = useState<string>("");

  const { screenData } = useFirebaseRemoteParams();
  useEffect(() => {
    if (screenData && screenData["stat_glossary"]) {
      setStatGlossary(JSON.parse(screenData["stat_glossary"]));
    }
  }, [screenData]);

  function dismiss() {
    setSearchTerm("")
    modal.current?.dismiss();
    toggle();
  }

  // Dynamically get categories from statGlossary
  const categories = statGlossary ? Object.keys(statGlossary) : [];
  const [selectedFilter, setSelectedFilter] = useState<string>(
    categories.length > 0 ? categories[0] : "",
  );
  const [expandedStat, setExpandedStat] = useState<number | null>(null);

  // Update selected filter when categories become available
  useEffect(() => {
    if (categories.length > 0 && !categories.includes(selectedFilter)) {
      setSelectedFilter(categories[0]);
    }
  }, [categories, selectedFilter]);

  const toggleStatExpansion = (index: number) => {
    setExpandedStat(expandedStat === index ? null : index);
  };

  // Handle filter change and reset expanded stat
  const handleFilterChange = (newFilter: string) => {
    setSelectedFilter(newFilter);
    setExpandedStat(null); // Reset expanded stat when filter changes
  };

  // Get stats for the selected filter category
  const getStatsForFilter = () => {
    if (!statGlossary) return [];
    if (searchTerm.trim()) {
      // When searching, look through all categories
      const allStats = Object.values(statGlossary).flat();
      return allStats.filter(
        (stat) =>
          stat.display_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase().trim()) ||
          stat.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase().trim())
      );
    }
    // When not searching, show only the selected category
    return statGlossary[selectedFilter] || [];
  };

  // Get filtered stats
  const filteredStats = getStatsForFilter();

  return (
    <IonModal
      onDidDismiss={dismiss}
      className={`full-modal`}
      isOpen={isOpen}
      backdropDismiss={false}
      ref={modal}
    >
      <>
        <IonHeader class="ion-no-border modal-ion-header">
          <IonToolbar className="custom-toolbar mt-[10px]">
            <div className="p-2 px-3 flex justify-between items-center sticky top-0 z-50">
              <div
                onClick={dismiss}
                className="flex items-center justify-center w-[32px] h-[32px] bg-white/10 rounded-full cursor-pointer"
              >
                <img
                  src={closeIcon}
                  alt="close"
                  className="w-[12.4px] h-[12.4px]"
                />
              </div>
              <IonTitle>
                <div className="flex flex-row items-center justify-center">
                  <img src={advancedStatsHeading} alt="advancedStatsHeading" />
                </div>
              </IonTitle>
            </div>
          </IonToolbar>

          <div className="py-5 px-3">
            <div className="flex flex-col justify-center space-y-[8px] items-center pt-5">
              <span className="text-[24px] font-mediumm text-white">
                Advanced Stats
              </span>
              <span className="text-[11px] text-white/75 text-center ">
                Your advanced stats library, ready for instant answers.
              </span>
            </div>

            <div className="relative w-full pt-8">
              <img
                src={advancedStatsSearch}
                alt="advancedStatsSearch"
                className="absolute left-4 pt-[15px]"
              />
              <input
                className="text-white bg-white/5 px-4 text-[14px] py-3 h-[48px] w-full rounded-[34px] pl-[43px] outline-none focus:outline-none resize-none overflow-y-auto placeholder:text-[11px] placeholder:text-white/50"
                placeholder="Search advanced stats...."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Horizontal scrollable filter bar - now using dynamic categories */}
            {categories.length > 0 && !searchTerm.trim() && (
              <HorizontalFilterBar
                filterItems={categories}
                selectedFilter={selectedFilter}
                onFilterChange={handleFilterChange}
              />
            )}

            {/* Content based on selected filter */}
            <div className="mt-4">
              <span className="text-white font-bold text-[16px]">
                {searchTerm.trim() ? 'Search Results' : selectedFilter}
              </span>
            </div>
          </div>
        </IonHeader>
        <IonContent>
          <div className="flex flex-col px-3 pb-10">
            {/* Error state - when not loading and no statGlossary */}
            {!statGlossary && (
              <div className="text-center py-4 text-white/75">
                Unable to load stats. Please try again later.
              </div>
            )}
            {/* Empty state */}
            {statGlossary && filteredStats.length === 0 && (
              <div className="text-center py-4 text-white/75">
                {searchTerm.trim()
                  ? "No matching stats found"
                  : "No stats available for this category"}
              </div>
            )}

            {/* Dynamic Stats List */}
            {filteredStats.map((stat:any, index) => (
              <div
                key={`${stat.column_name}-${index}`}
                className={`py-3 ${index === 0 ? "border-y" : "border-b"} border-white/5`}
                onClick={() => toggleStatExpansion(index)}
              >
                <div className="flex justify-between items-center">
                  <span className="text-white text-[12px] font-medium block">
                    {stat.display_name}
                  </span>
                  <motion.div
                    animate={{ rotate: expandedStat === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ marginRight: "10px" }}
                  >
                    <svg
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="white"
                        strokeOpacity="0.5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </motion.div>
                </div>

                <AnimatePresence>
                  {expandedStat === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <span className="text-white/75 text-[10px] mt-2 block">
                        {stat.description}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </IonContent>
      </>
    </IonModal>
  );
}
